/**
 * "Subscribe to updates."
 *
 * One field, because asking for anything more than an address to be told when a
 * service is down is a form people abandon.
 *
 * ── The response never says whether you were already subscribed ─────────────
 * The server answers `{ pending: true }` for a brand-new address and for one
 * that is already confirmed, and this component says the same sentence for
 * both. Anything else turns an anonymous endpoint into a membership oracle —
 * type an address, read the difference, learn whether that person subscribed.
 * The cost is that someone re-subscribing sees "check your inbox" and gets no
 * email, which is a far smaller problem.
 */

import React, { useState } from 'react';
import { ds } from '../ds';
import { api, errorText } from '../api';
import { Eyebrow, Footnote, Panel } from './Chrome';
import { display, monoPlain } from '../theme';

import { useT } from '../i18n';
type Phase = 'idle' | 'sending' | 'sent';

export function SubscribePanel() {
  const t = useT();
  const { Button, Input, Icon } = ds();
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (phase === 'sending') return;

    setPhase('sending');
    setError(null);
    try {
      await api.subscribe(email.trim());
      setPhase('sent');
    } catch (err) {
      setError(errorText(err, 'Could not subscribe you just now. Try again in a moment.'));
      setPhase('idle');
    }
  }

  if (phase === 'sent') {
    return (
      <Panel style={{ display: 'grid', gap: 6 }}>
        <h2
          style={{
            ...display(20),
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            color: 'var(--st-up-ink)'
          }}
        >
          <Icon name="mail" size={20} />
          {t('subscribe-panel.sent.title')}
        </h2>
        <Footnote>{t('subscribe-panel.sent.body')}</Footnote>
      </Panel>
    );
  }

  return (
    <Panel style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gap: 4 }}>
        <Eyebrow>{t('subscribe-panel.title')}</Eyebrow>
        <h2 style={display(20)}>{t('subscribe-panel.subtitle')}</h2>
        <Footnote>{t('subscribe-panel.body')}</Footnote>
      </div>

      <form
        onSubmit={submit}
        style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}
      >
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <Input
            type="email"
            label={t('subscribe-panel.field.label')}
            placeholder={t('subscribe-panel.field.placeholder')}
            value={email}
            autoComplete="email"
            invalid={!!error}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div style={{ paddingTop: 24 }}>
          <Button type="submit" variant="primary" disabled={phase === 'sending' || !email.trim()}>
            {phase === 'sending' ? 'Subscribing…' : 'Subscribe'}
          </Button>
        </div>
      </form>

      {error && (
        <span style={{ ...monoPlain, color: 'var(--st-down-ink)' }} role="alert">
          {error}
        </span>
      )}
    </Panel>
  );
}
